import { DownloadBookButton } from "../components/DownloadBookButton";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useIsPhone } from "../components/timeline/useIsPhone";

import { BOOKS, bookPath, chapterPath, mediumWords, SITE_TAGLINE } from "../config/site";
import type { BookHome } from "../data/load-book-home";
import { KeyItemEntries } from "../components/KeyItemEntries";
import { BookCharacters } from "./BookCharacters";
import { chapterNumber, formatChapterHeading } from "../lib/timeline/book/ChapterId";

const TABS = ["Chapters", "Characters", "Key items"] as const;

function counted(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

interface BookHeroProps {
  home: BookHome;
  span: string | null;
}

/** The book, as the compiled data describes it. */
export function BookHero({ home, span }: BookHeroProps) {
  const { summary, coverUrl } = home;
  const book = BOOKS.find((item) => item.id === summary.bookId) ?? BOOKS[0]!;
  const isPhone = useIsPhone();
  const [tab, setTab] = useState<(typeof TABS)[number]>("Chapters");
  const words = mediumWords(book);
  const credit = (
  <p className="book-credit">
    {book.credit} <Link to="/legal">Unofficial fan project.</Link>
  </p>
  );
  const chapters = (
  <nav className="book-chapters" aria-labelledby="book-chapters-title">
    <h2 id="book-chapters-title" className="book-section-title">{words.parts}</h2>
    {summary.chapters.map((chapter) => {
      const number = chapterNumber(chapter.id);
      return (
        <Link className="landing-card" key={chapter.id} to={chapterPath(book, number ?? 1)}>
          <span className="landing-card__label">{formatChapterHeading(chapter.id, chapter.title, ": ")}</span>
          <span className="landing-card__hint">{counted(chapter.scenes, "scene")}</span>
          <span className="landing-card__arrow" aria-hidden="true">→</span>
        </Link>
      );
    })}
  </nav>
  );
  return (
    <>
      <header className="book-hero">
        {coverUrl === undefined ? null : (
          <img className="book-hero__cover" src={coverUrl} alt={`Cover of ${summary.title}`} />
        )}
        <div className="book-hero__text">
          <h1 className="book-hero__title">{summary.title}</h1>
          <p className="book-hero__lead">
            {SITE_TAGLINE}
          </p>
          {span === null ? null : <p className="book-hero__span">{span}</p>}
          <ul className="book-hero__stats" aria-label="About this book">
            <li>{counted(summary.chapters.length, words.part)}</li>
            <li>{counted(summary.scenes, "scene")}</li>
            <li>{counted(summary.characters, "character")}</li>
            <li>{counted(summary.places, "place")}</li>
          </ul>
          <Link className="book-hero__play" to={bookPath(book)}>Play the whole {words.whole}</Link>
          <DownloadBookButton bookId={book.id} />
        </div>
      </header>

      {isPhone ? (
        <>
          <div className="book-tabs" role="tablist" aria-label="About this book">
            {TABS.map((name) => (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={tab === name}
                onClick={() => setTab(name)}
              >
                {name}
              </button>
            ))}
          </div>
          {tab === "Chapters" ? chapters : null}
          {tab === "Characters" ? <BookCharacters book={book} /> : null}
          {tab === "Key items" ? <KeyItemEntries books={[book]} /> : null}
          {credit}
        </>
      ) : (
        <>
          <BookCharacters book={book} />
          <KeyItemEntries books={[book]} />
          {credit}
          {chapters}
        </>
      )}
    </>
  );
}
